import { Box, Drawer } from '@mui/material';
import { assertNotNull, sleep } from '@remote-voice/utilities';
import { useAtom } from 'jotai';
import Cookies from 'js-cookie';
import Enumerable from 'linq';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { selectedMicrophoneDeviceIdAtom } from '@/components/atoms/AudioDeviceAtom';

import { useFontSize, useSetFontSize } from '@/components/hooks/useFontSize';
import { useSetLanguage } from '@/components/hooks/useLanguage';
import useLoadingBackdrop from '@/components/hooks/useLoadingBackdrop';
import useOpenFile from '@/components/hooks/useOpenFile';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import { useSetUserName, useUserName } from '@/components/hooks/useUserName';
import useAudioTest from '@/components/organisms/audioTest/useAudioTest';
import ChatBody from '@/components/organisms/chat/ChatBody';
import ChatConsecutiveVoiceDialog from '@/components/organisms/chat/ChatConsecutiveVoiceDialog';
import ChatFooter from '@/components/organisms/chat/ChatFooter';
import { TemplateMicPosition } from '@/components/organisms/chat/ChatFooterMic';
import { MyMessagePosition } from '@/components/organisms/chat/ChatFooterMicDual';
import ChatFooterReply from '@/components/organisms/chat/ChatFooterReply';
import ChatHeader from '@/components/organisms/chat/ChatHeader';
import ChatHeaderRemote from '@/components/organisms/chat/ChatHeaderRemote';
import ChatMenu from '@/components/organisms/chat/ChatMenu';
import { ChatReaction } from '@/components/organisms/chat/ChatReactionList';
import SubUserLanguageSelect from '@/components/organisms/chat/SubUserLanguageSelect';
import useChat from '@/components/organisms/chat/useChat';
import { useChatroomsWaitingGuestsEnter } from '@/components/organisms/chat/useChatroomsWaitingGuestsEnter';
import useChatUserListDialog from '@/components/organisms/chat/useChatUserListDialog';
import useLeaveChatRoomDialog from '@/components/organisms/chat/useLeaveChatRoomDialog';
import useChatEnvironmentSettingsDialog from '@/components/organisms/chatEnvironmentSettingsDialog/useChatEnvironmentSettingsDialog';
import useChatFontSettingsDialog from '@/components/organisms/chatFontSettingsDialog/useChatFontSettingsDialog';
import useChatUserSettingsDialog from '@/components/organisms/chatUserSettingsDialog/useChatUserSettingsDialog';
import useConfirmContinueDialog from '@/components/organisms/confirmDialog/useConfirmContinueDialog';
import useConfirmOkDialog from '@/components/organisms/confirmDialog/useConfirmOkDialog';
import useInvitingRoomDialog from '@/components/organisms/invitingRoomDialog/useInvitingRoomDialog';
import useProjectionModeDialog from '@/components/organisms/projectionModeDialog/useProjectionModeDialog';
import useReverseTranslationDialog from '@/components/organisms/reverseTranslationDialog/useReverseTranslationDialog';
import ChatThemeProvider from '@/providers/ChatThemeProvider';
import {
  ChatUserRole,
  TranslateMethod,
  TtsService,
  useNotifyLeaveMutation,
} from '@/types/graphql';
import { isChatEditor, isChatHost } from '@/utils/userRole';

// useChatのロジックを、各種プレゼンテーションコンポーネントと接続する
const Chat = (props: {
  ttsService: TtsService;
  availableNamisenseLanguages: string[];
  availableGcpLanguages: string[];
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('chat');

  useEffect(() => {
    // このコンポーネントの中では直接使わないが，一度コールしてHowlを有効化する
    // 以下の行がないとチャット画面をリロードした際にWeb Audio APIのセキュリティを突破できない
    new Howl({
      src: ['/sample_audio.mp3'],
      onunlock: () => console.log('audio unlocked'),
    });
  }, []);

  const micStartRef = useRef<(() => Promise<void>) | undefined>();
  const micEndRef = useRef<(() => void) | undefined>();
  const audioDeviceEventCallbacks = useMemo(
    () => ({
      onEnded: () => micEndRef.current?.(),
      onMuted: () => micEndRef.current?.(),
      onUnmuted: () => micStartRef.current?.(),
    }),
    []
  );

  const {
    userId,
    userPassword,
    chatRoomId,
    sessionEntryCode,
    language,
    subUserLanguage,
    setSubUserLanguage,
    reverseLanguage,
    setReverseLanguage,
    chatUserRole,
    sessionInfo,
    users,
    joiningUsers,
    isQuestionMode,
    isVoiceMode,

    sendMessage,
    removeMessage,
    sendTemplate,
    sendImages,
    clearMessages,
    setReaction,
    removeUser,
    endSession,
    setFlag,
    setQuestionMode,
    setVoiceMode,
    setChatEnvironmentSettings,

    messages,
    loadPreviousMessages,
    getPreviousMessage,

    chatText,
    setChatText,
    isMicMode,
    setIsMicMode,
    isTemplateMode,
    isRemoteMode,
    setIsTemplateMode,
    templateMessages,
    isSendingMessage,

    playMessageEnabled,
    setPlayMessageEnabled,
    playChat,
    stopChat,
    mini,
    playingMessageId,

    replaceNgWord,

    setReplyInfo,
    replyUser,
    replyText,
    isReplyGuestSend,
    isReplyMyMessage,

    loading,
    loadingPrevious,

    isHiddenInformation,
    setIsHiddenInformation,
    onChangeRemoteMode,
    onChangeRemoteModeDual,
    isOnReverseTranslation,
    setIsOnReverseTransalation,
    canLoadPrevious,

    selectedProjectionLanguage,
    setSelectedProjectionLanguage,
    isProjectionMode,
  } = useChat({ ttsService: props.ttsService, audioDeviceEventCallbacks });

  const loadingBackdrop = useLoadingBackdrop();
  const confirmDialog = useConfirmContinueDialog();
  const okDialog = useConfirmOkDialog();
  const invitingRoomDialog = useInvitingRoomDialog();
  const userListDialog = useChatUserListDialog(users);
  const leaveChatRoomDialog = useLeaveChatRoomDialog();
  const chatFontSettingsDialog = useChatFontSettingsDialog();
  const chatUserSettingsDialog = useChatUserSettingsDialog();
  const chatEnvironmentSettingsDialog = useChatEnvironmentSettingsDialog();
  const revTranslationDialog = useReverseTranslationDialog();
  const projectionModeDialog = useProjectionModeDialog();
  const audioTest = useAudioTest();
  const showSnackbar = useSnackbar();
  const canGuideControl = isChatHost(chatUserRole);

  // 参加者一覧ダイアログの表示
  const showUserListDialog = useCallback(() => {
    userListDialog.open({
      guideOptions: canGuideControl
        ? {
            chatRoomId: chatRoomId,
            entryCode: sessionEntryCode,
            userId: userId,
            userPassword: userPassword,
          }
        : undefined,
    });
  }, [
    chatRoomId,
    canGuideControl,
    sessionEntryCode,
    userId,
    userListDialog,
    userPassword,
  ]);
  // 待機室入室時の通知
  useChatroomsWaitingGuestsEnter({
    onNotifyClick: useCallback(
      (roomId: string) => {
        if (roomId === chatRoomId) showUserListDialog();
      },
      [chatRoomId, showUserListDialog]
    ),
  });
  const [notifyLeave] = useNotifyLeaveMutation();
  const openFile = useOpenFile();
  const [canUseMic] = useState(() => Cookies.get('RV_CAN_USE_MIC') === 'true');
  const [openMenu, setOpenMenu] = useState(false);

  //use in dual lang mode, my message position default is left
  const [myMessagePosition, setMyMessagePosition] =
    useState<MyMessagePosition>('start');
  // 定型文表示中のマイクの左右位置。テキスト入力へ切り替えても保持する
  const [templateMicPosition, setTemplateMicPosition] =
    useState<TemplateMicPosition>('right');
  const [reactions, setReactions] = useState<ChatReaction[]>();

  const [isOpenSubUserLanguageDialog, setIsOpenSubUserLanguageDialog] =
    useState(false);
  const { t: tCommon } = useTranslation('common');

  const [isGuestTyping, setIsGuestTyping] = useState(false);

  const fontSize = useFontSize();
  const setFontSize = useSetFontSize();
  const userName = useUserName();
  const setUserName = useSetUserName();
  const setLanguage = useSetLanguage();
  const isReadyMic =
    mini.initializing === false && mini.ending === false && sessionInfo != null;
  const micUsing = isReadyMic === false || mini.recording;
  const micUsingRef = useRef(micUsing);
  useEffect(() => {
    micUsingRef.current = micUsing;
  }, [micUsing]);

  const translateMethod = sessionInfo?.translateMethod;
  const keepTranslateMethod = useRef(translateMethod);
  const [selectedMicrophoneDeviceId] = useAtom(selectedMicrophoneDeviceIdAtom);

  const startVoiceRecognition = mini.startVoiceRecognition;
  micStartRef.current = useCallback(
    async (isGuestRecording?: boolean) => {
      const currentLanguage = isGuestRecording ? subUserLanguage : language;
      const useNamisense =
        props.availableNamisenseLanguages.includes(currentLanguage);
      const useGcp = props.availableGcpLanguages.includes(currentLanguage);

      assertNotNull(sessionInfo);
      try {
        keepTranslateMethod.current = translateMethod;
        await startVoiceRecognition({
          deviceId: selectedMicrophoneDeviceId,
          roomId: chatRoomId,
          smoothingTimeConstant: sessionInfo.vadSmoothingTimeConstant,
          energyOffset: sessionInfo.vadEnergyOffset,
          energyThresholdRatioPos: sessionInfo.vadEnergyThresholdRatioPos,
          energyThresholdRatioNeg: sessionInfo.vadEnergyThresholdRatioNeg,
          energyIntegration: sessionInfo.vadEnergyIntegration,

          // 逐次の場合は自動継続しない
          continuous:
            translateMethod === TranslateMethod.Consecutive ? false : true,
          // 逐次の場合は音声入力の終了検知も行わない
          // namisenseを利用する言語の場合も終了検知をおこなわない
          disableVad:
            translateMethod === TranslateMethod.Consecutive ||
            useNamisense ||
            useGcp
              ? true
              : false,

          splitTextSendIntervalMs: 1000, // sessionInfo.splitTextSendIntervalMs,
          isSplitTextEnabled: false, // sessionInfo.isSplitTextEnabled,
          isUseLlmForSplitText: false, // sessionInfo.isUseLlmForSplitText,
          isUseNamisense: useNamisense,
          isUseGcp: useGcp,

          micEchoCancellation: undefined, // sessionInfo.micEchoCancellation ?? undefined,
          micNoiseSuppression: undefined, // sessionInfo.micNoiseSuppression ?? undefined,

          namiSenseVadThreshold: sessionInfo.vadThreshold, // sessionInfo.namiSenseVadThreshold,
          namiSenseVadVoicedThreshold: sessionInfo.vadVoicedThreshold, // sessionInfo.namiSenseVadVoicedThreshold,
          namiSenseVadUnvoicedThreshold: sessionInfo.vadUnvoicedThreshold, // sessionInfo.namiSenseVadUnvoicedThreshold,

          saveAudioProxy: false, // sessionInfo.enableAudioSaving,
          onSocketError: (e) => {
            console.error('Voice start failed: ', e);
            showSnackbar('error', t('voiceStartFailed'));
          },
          onSttError: (e) => {
            console.error('Voice recognition failed: ', e);
            // showSnackbar('error', t('voiceRecognitionFailed'));
          },
          isGuestRecording,
        });
      } catch (e) {
        console.error('Voice start failed: ', e);
        showSnackbar('error', t('voiceStartFailed'));
      }
    },
    [
      props.availableNamisenseLanguages,
      props.availableGcpLanguages,
      subUserLanguage,
      language,
      sessionInfo,
      translateMethod,
      startVoiceRecognition,
      selectedMicrophoneDeviceId,
      chatRoomId,
      showSnackbar,
      t,
    ]
  );

  const endVoiceRecognition = mini.endVoiceRecognition;
  micEndRef.current = useCallback(() => {
    if (
      keepTranslateMethod.current === TranslateMethod.Consecutive ||
      keepTranslateMethod.current === TranslateMethod.Simultaneous
    ) {
      endVoiceRecognition();
    }
  }, [endVoiceRecognition]);
  const cancelVoiceRecognition = mini.cancelVoiceRecognition;
  const micCancel = useCallback(() => {
    if (
      keepTranslateMethod.current === TranslateMethod.Consecutive ||
      keepTranslateMethod.current === TranslateMethod.Simultaneous
    ) {
      cancelVoiceRecognition();
    }
  }, [cancelVoiceRecognition]);

  const onClickTemplateMessage = isReadyMic
    ? () => {
        setOpenMenu(false);
        setChatText('');
        // 定型文モードはマイクモードとセットの画面のため、テキスト入力中に押された場合もマイクモードへ切り替える
        setIsMicMode(true);
        setIsTemplateMode(!isTemplateMode);
        setReactions(undefined);
      }
    : undefined;

  const onClickSendImage = useCallback(
    (isSubUserSend?: boolean) =>
      openFile({
        accept:
          'image/jpg,image/jpeg,image/png,image/heic,.jpg,.jpeg,.png,.heic',
        multiple: true,
        onOpen: async (fileList) => {
          // アップロード先取得
          await sendImages(
            Enumerable.range(0, fileList.length)
              .select((x) => fileList[x])
              .toArray(),
            isSubUserSend
          );
        },
      }),
    [openFile, sendImages]
  );

  // 録音中ダイアログの表示条件整理。
  // 録音以外の送信で誤動作しないようにするため、isRecordingからisTranslatingまでの間 true にする。
  const [isRecordingToTranslating, setIsRecordingToTranslating] =
    useState(false);
  useEffect(() => {
    if (mini.recording) {
      setIsRecordingToTranslating(true);
    }
  }, [mini.recording]);
  useEffect(() => {
    if (mini.recording === false && isSendingMessage === false && isReadyMic) {
      setIsRecordingToTranslating(false);
    }
  }, [mini.recording, isReadyMic, isSendingMessage]);
  const showRecordingDialog =
    translateMethod === TranslateMethod.Consecutive && isRecordingToTranslating;
  const recordingDialogPage = isSendingMessage
    ? 'translating'
    : mini.recording && isReadyMic
    ? 'recording'
    : 'recognizing';

  const getUser = useCallback(
    (userId: string) => users.find((x) => x.userId === userId),
    [users]
  );

  const onClickLeaveSession = async () => {
    setOpenMenu(false);
    const leaveResult = await leaveChatRoomDialog.open({
      showExitSessionCheck: canGuideControl, // ガイドの場合はチャットセッション自体のクローズ有無選択
    });
    if (leaveResult === 'cancel') {
      return;
    }
    // セッション終了処理
    if (leaveResult === 'exit') {
      // 但しガイドが他にいた場合は、セッション終了せず参加者の全退出のみ
      if (joiningUsers.filter((x) => isChatHost(x.userRole)).length >= 2) {
        await removeUser(undefined);
      } else {
        await endSession();
      }
    } else {
      await notifyLeave({
        variables: {
          input: {
            chatRoomId,
            sessionEntryCode,
            userId,
            userPassword,
          },
        },
      });
    }
    // 退出・リダイレクト処理
    if (chatUserRole === ChatUserRole.Guide) {
      // navigate(
      //   `/chat/${chatRoomId}/?chatUserId=${userId}&chatUserPassword=${userPassword}`
      // );
      navigate('/chat/thanks');
    } else if (chatUserRole === ChatUserRole.TenantCompanyAdmin) {
      navigate('/admin/room');
      // navigate('/chat/thanks?role=guide');
    } else {
      const serveyUrl = sessionInfo?.surveyUrl;
      if (serveyUrl) {
        window.location.replace(serveyUrl);
      } else {
        navigate('/chat/thanks');
      }
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateRows="auto 1fr auto"
      height={1}
      sx={{
        bgcolor: (t) =>
          isProjectionMode ? '#dbe2e5' : t.palette.background.default,
        userSelect: 'none',
      }}
    >
      <Drawer open={openMenu} onClose={() => setOpenMenu(false)}>
        <ChatMenu
          userRole={chatUserRole ?? ChatUserRole.Guest}
          userCount={joiningUsers.length}
          dualLangTranslation={!!sessionInfo?.dualLangTranslation}
          isGuestInputRestricted={
            (sessionInfo?.isGuestInputRestricted ?? false) || !isQuestionMode
          }
          onClickParticipants={() => {
            setOpenMenu(false);
            showUserListDialog();
          }}
          // onClickMuteAllUser={async () => {
          //   setOpenMenu(false);
          //   if (
          //     await confirmDialog.open({
          //       title: t('menu.muteAllMicsTitle'),
          //       okLabel: t('menu.muteAllMicsOk'),
          //       okColor: 'warning',
          //     })
          //   ) {
          //     muteUserMic(undefined);
          //   }
          // }}
          onClickRemoveAllUser={async () => {
            setOpenMenu(false);
            if (
              await confirmDialog.open({
                title: t('menu.removeAllUsersTitle'),
                okLabel: t('menu.removeAllUsersOk'),
                okColor: 'warning',
              })
            ) {
              removeUser(undefined);
            }
          }}
          // onClickTemplateMessage={onClickTemplateMessage}
          playMessageEnabled={playMessageEnabled}
          isOnReverseTranslation={isOnReverseTranslation}
          onClickTogglePlayMessageEnabled={() => {
            if (!isVoiceMode) return;
            const newEnabled = !playMessageEnabled;
            setPlayMessageEnabled(newEnabled);
            if (newEnabled) {
              showSnackbar('success', t('voiceReadingToOn'));
            } else {
              showSnackbar('success', t('voiceReadingToOff'));
            }
          }}
          onClickToggleDisplayInformation={() => {
            setIsHiddenInformation(!isHiddenInformation);
          }}
          isHiddenInformation={isHiddenInformation}
          isVoiceMode={isVoiceMode}
          onClickProjectionMode={async () => {
            setOpenMenu(false);
            const selected = await projectionModeDialog.open({
              defaultLanguages: selectedProjectionLanguage,
            });
            if (selected != null) {
              setSelectedProjectionLanguage(
                selected.language,
                selected.firstItemIsAutoPlay
              );
            }
          }}
          onClickSwitchReverseTranslation={(value: boolean) => {
            setIsOnReverseTransalation(value);
          }}
          onClickReverseTranslation={async () => {
            setOpenMenu(false);
            const selected = await revTranslationDialog.open({
              defaultRevLanguage: reverseLanguage,
              // 参加中のユーザーのうち自分の選択言語以外の言語をリストに出す
              enableRevLanguages: Array.from(
                new Set(joiningUsers.map((user) => user.language))
              ).filter((lang) => lang !== language),
            });
            if (selected !== null) setReverseLanguage(selected);
          }}
          onClickChatSettings={async () => {
            setOpenMenu(false);
            const result = await chatUserSettingsDialog.open({
              defaultValue: { userName, language },
            });
            if (result) {
              setUserName(result.userName);
              setLanguage(result.language);
            }
          }}
          onClickFontSettings={async () => {
            setOpenMenu(false);
            const result = await chatFontSettingsDialog.open({
              defaultValue: fontSize,
            });
            if (result) {
              setFontSize(result);
            }
          }}
          onClickDeviceTest={() => {
            setOpenMenu(false);
            audioTest();
          }}
          onClickVadSettings={async () => {
            setOpenMenu(false);
            const result = await chatEnvironmentSettingsDialog.open({
              defaultValue: {
                vadPresetId: sessionInfo?.vadPresetId ?? '',
              },
              chatRoomId,
              sessionEntryCode,
              userId,
              userPassword,
            });
            if (result) {
              await loadingBackdrop.open(() =>
                setChatEnvironmentSettings(result)
              );
            }
          }}
          onClickSendImage={onClickSendImage}
          onClickClearMessages={async () => {
            setOpenMenu(false);
            if (
              await confirmDialog.open({
                title: t('menu.clearScreenTitle'),
                message: t('menu.clearScreenMessage'),
                okLabel: t('menu.clearScreenOk'),
              })
            ) {
              clearMessages();
            }
          }}
          onClickLeaveSession={onClickLeaveSession}
        />
      </Drawer>
      {isRemoteMode ? (
        <ChatHeaderRemote
          onChangeRemoteMode={onChangeRemoteMode}
          onChangeRemoteModeDual={onChangeRemoteModeDual}
        />
      ) : (
        <ChatHeader
          roomName={sessionInfo?.roomName ?? ''}
          joiningUsersCount={joiningUsers.length}
          waitingUsersCount={
            canGuideControl
              ? users.filter((x) => x.isJoining && x.isWaiting).length
              : 0
          }
          onOpenMenu={() => setOpenMenu(true)}
          onClickParticipants={
            isChatEditor(
              sessionInfo?.isGuestInputRestricted ?? false,
              chatUserRole
            )
              ? showUserListDialog
              : undefined
          }
          isQuestionMode={canGuideControl ? isQuestionMode : undefined}
          isVoiceMode={canGuideControl ? isVoiceMode : undefined}
          onChangeQuestionMode={(mode) => {
            setQuestionMode(mode); // ローカルのフラグセットはsubscription経由で行われる
          }}
          onChangeVoiceMode={(mode) => {
            setVoiceMode(mode); // ローカルのフラグセットはsubscription経由で行われる
          }}
          isProjectionMode={isProjectionMode}
          canGuideControl={canGuideControl}
          onClickLeaveSession={onClickLeaveSession}
        />
      )}

      <ChatThemeProvider>
        <ChatBody
          chatRoomId={chatRoomId}
          sessionEntryCode={sessionEntryCode}
          userId={userId}
          language={language}
          subUserLanguage={subUserLanguage}
          reverseLanguage={reverseLanguage}
          isOnReverseTranslation={isOnReverseTranslation}
          canGuideControl={canGuideControl}
          dualLangTranslation={sessionInfo?.dualLangTranslation}
          myMessagePosition={myMessagePosition}
          userRole={chatUserRole ?? ChatUserRole.Guest}
          //
          onClickInvite={() => {
            setOpenMenu(false);
            invitingRoomDialog.open(
              {
                invitation: {
                  url: `${location.origin}/chat/${chatRoomId}`,
                  entryCode: sessionEntryCode,
                },
              },
              (c) => <ChatThemeProvider>{c}</ChatThemeProvider>
            );
          }}
          onClickClearMessages={async () => {
            setOpenMenu(false);
            if (
              await confirmDialog.open({
                title: t('menu.clearScreenTitle'),
                message: t('menu.clearScreenMessage'),
                okLabel: t('menu.clearScreenOk'),
              })
            ) {
              clearMessages();
            }
          }}
          onClickLeaveSession={onClickLeaveSession}
          loading={loading}
          loadingPrevious={loadingPrevious}
          canLoadPrevious={canLoadPrevious}
          messages={messages}
          getIsContinuedMessage={(key, msg) => {
            if (sessionInfo?.dualLangTranslation) {
              return (
                getPreviousMessage(key)?.[1].userId === msg.userId && // 次のメッセージが同じユーザーであれば継続とする
                getPreviousMessage(key)?.[1].isSubUserSend === msg.isSubUserSend
              );
            } else {
              return getPreviousMessage(key)?.[1].userId === msg.userId; // 次のメッセージが同じユーザーであれば継続とする
            }
          }}
          getUser={getUser}
          loadPrevious={loadPreviousMessages}
          isGuestInputRestricted={sessionInfo?.isGuestInputRestricted ?? false}
          onEditMessage={async (message, isSubUserSend) => {
            if (mini.recording) {
              await okDialog.open({
                okLabel: t('editMessage.warningOk'),
                title: t('editMessage.warningOnRecording'),
              });
              return;
            }
            if (
              chatText !== '' &&
              chatText !== message &&
              (await confirmDialog.open({
                title: t('editMessage.confirm'),
                okLabel: t('editMessage.confirmOK'),
              })) === false
            ) {
              return;
            }
            setChatText(message);
            setIsGuestTyping(isSubUserSend);
            setIsMicMode(false);
            setIsTemplateMode(false);
            setReactions(undefined);
            await sleep(0); // 文字が置き換わるのをsleepで待機
            document.getElementById('chat-input-text-area')?.focus();
          }}
          onDeleteMessage={removeMessage}
          onReplyMessage={async (id, userId, text, isSubUserSend, isMine) => {
            setReplyInfo({ id, text, userId, isSubUserSend, isMine });
            // setIsMicMode(false);
            setIsTemplateMode(false);
            setReactions(undefined);
            await sleep(0); // 文字が置き換わるのをsleepで待機
            document.getElementById('chat-input-text-area')?.focus();
          }}
          onReaction={setReaction}
          onOpenReaction={(reactions) => {
            setReactions(reactions);
            setIsMicMode(true);
          }}
          onPlayMessage={playMessageEnabled ? playChat : undefined}
          onStopMessage={stopChat}
          onCheckFlag={
            canGuideControl
              ? async (commandId, check) => {
                  await setFlag(commandId, check);
                  if (check) {
                    showSnackbar('success', t('flagAdded'));
                  } else {
                    showSnackbar('success', t('flagRemoved'));
                  }
                }
              : undefined
          }
          playingMessageId={playingMessageId}
          replaceNgWord={replaceNgWord}
          reactionIsOnlyQuestion={!isQuestionMode && !canGuideControl}
          isHiddenInformation={isHiddenInformation}
        />
        {isOpenSubUserLanguageDialog && (
          <SubUserLanguageSelect
            open={isOpenSubUserLanguageDialog}
            language={subUserLanguage}
            onClose={(lang) => {
              setIsOpenSubUserLanguageDialog(false);
              if (lang) {
                setSubUserLanguage(lang);
              }
            }}
            tCommon={tCommon}
          />
        )}
        {replyText != null && (
          <ChatFooterReply
            reply={replyText}
            onCancelReply={() => setReplyInfo(undefined)}
            userName={replyUser?.userName ?? ''}
            userRole={chatUserRole ?? ChatUserRole.Guest}
            sx={isMicMode ? { pb: '22px' } : undefined}
            canGuideControl={canGuideControl}
            isSubUserSend={isReplyGuestSend}
            dualLangTranslation={sessionInfo?.dualLangTranslation}
            isMine={isReplyMyMessage}
          />
        )}
        {(canGuideControl ||
          isChatEditor(
            (sessionInfo?.isGuestInputRestricted ?? false) || !isQuestionMode,
            chatUserRole
          )) &&
          !isProjectionMode && (
            <ChatFooter
              chatRoomId={chatRoomId}
              sessionEntryCode={sessionEntryCode}
              userId={userId}
              userPassword={userPassword}
              language={language}
              subUserLanguage={subUserLanguage}
              isGuestTyping={isGuestTyping}
              setIsGuestTyping={(value: boolean) => {
                setIsGuestTyping(value);
              }}
              //
              chatText={chatText}
              onChangeChatText={setChatText}
              isMicMode={isMicMode}
              isRemoteMode={isRemoteMode}
              dualLangTranslation={sessionInfo?.dualLangTranslation}
              canGuideControl={canGuideControl}
              onChangeMyMessagePosition={(value) => {
                setMyMessagePosition(value);
              }}
              myMessagePosition={myMessagePosition}
              templateMicPosition={templateMicPosition}
              onChangeTemplateMicPosition={setTemplateMicPosition}
              onChangeRemoteMode={onChangeRemoteMode}
              onChangeRemoteModeDual={onChangeRemoteModeDual}
              onChangeMicMode={(v) => {
                setIsMicMode(v);
                setReactions(undefined);
                // setReplyId(undefined);
                // setReplyText(undefined);
              }}
              onOpenLanguageDialog={(v) => {
                setIsOpenSubUserLanguageDialog(v);
              }}
              isTemplateMode={isTemplateMode && templateMessages != null}
              onChangeTemplateMode={(v) => {
                setIsTemplateMode(v);
                setReactions(undefined);
                setReplyInfo(undefined);
              }}
              templateMessages={templateMessages ?? []}
              isReactionMode={reactions != null}
              reactions={reactions}
              onChangeReactionMode={(v) => {
                if (!v) setReactions(undefined);
              }}
              //
              onSendMessage={(text, isSubUserSend) => {
                sendMessage(text, isSubUserSend);
                setReplyInfo(undefined);
              }}
              onSendTemplate={sendTemplate}
              onSendImage={(isSubUserSend?: boolean) => {
                onClickSendImage(isSubUserSend);
              }}
              onClickTemplateMessage={
                canGuideControl ? onClickTemplateMessage : undefined
              }
              canUseMic={
                canUseMic &&
                (sessionInfo == null || mini.initializing
                  ? true
                  : mini.canVoiceRecognition)
              }
              isSimultaneous={translateMethod === TranslateMethod.Simultaneous}
              isRecording={mini.recording}
              isReadyMic={isReadyMic}
              onMicStart={micStartRef.current}
              onMicEnd={micEndRef.current}
              onMicCancel={micCancel}
            />
          )}
        <ChatConsecutiveVoiceDialog
          open={showRecordingDialog}
          onStopRecording={endVoiceRecognition}
          onCancelRecording={cancelVoiceRecognition}
          page={recordingDialogPage}
        />
      </ChatThemeProvider>
      {playingMessageId != null && (
        <div id="fps-slowdown-make-sound-noisy"></div>
      )}
    </Box>
  );
};
export default Chat;
